import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import useReveal from "../lib/useReveal";
import Seo from "../components/Seo";
import { getPostBySlug, getRelatedPosts } from "../lib/blog";

function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
}

function Block({ block }) {
    if (block.type === "h2") {
        return (
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1A1A1A] mt-12 mb-4 leading-[1.1]">
                {block.text}
            </h2>
        );
    }
    if (block.type === "h3") {
        return (
            <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-[#1B4332] mt-8 mb-3 leading-[1.15]">
                {block.text}
            </h3>
        );
    }
    if (block.type === "cta") {
        return (
            <div className="my-12 bg-[#F3F1EC] border-l-4 border-[#C84B31] px-6 py-6 md:px-8 md:py-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <p className="font-display text-2xl md:text-3xl leading-[1.15] text-[#1A1A1A]">
                    {block.text}
                </p>
                <Link
                    to={block.linkTo || "/enquiry"}
                    data-testid="blog-inline-cta"
                    className="inline-flex items-center gap-3 bg-[#1B4332] text-[#FAF9F6] px-6 py-3 text-[11px] uppercase tracking-[0.26em] hover:bg-[#112d21] transition-all whitespace-nowrap"
                >
                    {block.linkText || "Get in touch"}
                    <ArrowUpRight size={14} />
                </Link>
            </div>
        );
    }
    return (
        <p className="text-lg font-light text-[#1A1A1A]/85 leading-[1.75] mt-4">{block.text}</p>
    );
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);
    useReveal();
    useEffect(() => window.scrollTo(0, 0), [slug]);

    if (!post) return <Navigate to="/blog" replace />;

    const related = getRelatedPosts(slug);

    return (
        <main data-testid="blog-post-page" className="pt-32 md:pt-40">
            <Seo
                title={`${post.title} | The Food Gala Journal`}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                image={post.cover}
            />

            {/* HERO */}
            <section className="max-w-[1000px] mx-auto px-6 md:px-10 pb-10 reveal">
                <Link
                    to="/blog"
                    data-testid="blog-back-link"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#1B4332]/75 hover:text-[#1B4332] mb-8"
                >
                    <ArrowLeft size={14} /> Back to Journal
                </Link>
                <div className="flex items-center gap-3 mb-6">
                    <span className="text-[10px] uppercase tracking-[0.32em] bg-[#1B4332] text-[#FAF9F6] px-3 py-1">
                        {post.category}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">
                        {formatDate(post.date)}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">·</span>
                    <span className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55">
                        {post.readTime}
                    </span>
                </div>
                <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
                    {post.title}
                </h1>
                <p className="mt-6 text-xl font-light text-[#1A1A1A]/75 leading-relaxed">
                    {post.excerpt}
                </p>
                <div className="mt-6 text-[11px] uppercase tracking-[0.28em] text-[#1B4332]/70">
                    By {post.author}
                </div>
            </section>

            {/* COVER */}
            <section className="max-w-[1200px] mx-auto px-6 md:px-10 mb-12 md:mb-16 reveal">
                <div className="aspect-[16/9] overflow-hidden">
                    <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
                </div>
            </section>

            {/* BODY */}
            <article className="max-w-[760px] mx-auto px-6 md:px-10 pb-16 md:pb-20" data-testid="blog-post-body">
                {post.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                ))}

                {/* Tags */}
                <div className="mt-12 pt-8 border-t border-black/10 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.26em] text-[#1B4332]/75">
                    {post.tags.map((t) => (
                        <span key={t} className="border border-[#1B4332]/25 px-3 py-1.5">
                            #{t}
                        </span>
                    ))}
                </div>
            </article>

            {/* RELATED */}
            {related.length > 0 && (
                <section className="bg-[#F3F1EC] py-16 md:py-20" data-testid="blog-related">
                    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-8 reveal">
                            Keep Reading
                        </div>
                        <div className="grid md:grid-cols-2 gap-8 md:gap-10">
                            {related.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`blog-related-${p.slug}`}
                                    className="group bg-[#FAF9F6] border border-[#1B4332]/10 hover:border-[#1B4332] transition-all duration-500 reveal"
                                >
                                    <div className="zoom-wrap aspect-[16/10] overflow-hidden">
                                        <img
                                            src={p.cover}
                                            alt={p.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-6 md:p-7">
                                        <div className="text-[10px] uppercase tracking-[0.32em] text-[#C84B31] mb-3">
                                            {p.category}
                                        </div>
                                        <h3 className="font-display text-2xl leading-[1.15] tracking-tight group-hover:text-[#1B4332] transition-colors">
                                            {p.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </main>
    );
}
